import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logoutAsync } from '../../redux/slices/authSlice';
import { getMyChats } from '../../services/chatService';
import { getCurrentUser } from '../../services/authService';
import logo from '../../assets/travel-friend-logo.png';

import {
  FaBell,
  FaEnvelope,
  FaUserCircle,
  FaSignOutAlt,
  FaCommentDots,
  FaHome,
  FaUsers,
  FaComments,
  FaUserFriends,
  FaGlobeAmericas,
  FaRobot,
  FaCompass,
  FaMapMarkedAlt,
  FaChevronDown,
  FaMoon,
  FaSun,
  FaUserPlus,
  FaUsersCog
} from 'react-icons/fa';

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) fetchUnreadCounts();

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated, user]);

  // Close menus when route changes or click outside
  useEffect(() => {
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.profile-menu-container')) {
        setIsProfileMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCounts = async () => {
    try {
      const chats = await getMyChats();
      let count = 0;

      chats?.forEach(chat =>
        chat.messages?.forEach(msg => {
          if (!msg.readBy?.some(r => r.user === user._id)) count++;
        })
      );
      setUnreadMessageCount(count);

      const userData = await getCurrentUser();
      const unread = userData?.user?.notifications?.filter(n => !n.read) || [];
      setUnreadNotificationCount(unread.length);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = async () => {
    await dispatch(logoutAsync());
    navigate('/login');
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    // You can implement actual dark mode logic here
  };

  // Navigation items with icons and labels
  const navItems = [
    { to: '/dashboard', icon: <FaHome />, label: 'Dashboard', color: 'from-indigo-500 to-blue-500' },
    { to: '/groups', icon: <FaUserFriends />, label: 'Groups', color: 'from-emerald-500 to-teal-500' },
    { to: '/chat', icon: <FaGlobeAmericas />, label: 'Public Chat', color: 'from-cyan-500 to-sky-500' },
    { to: '/private-chat', icon: <FaCommentDots />, label: 'Private Chat', color: 'from-purple-500 to-pink-500' },
    { to: '/invites', icon: <FaEnvelope />, label: 'Invites', color: 'from-amber-500 to-orange-500' },
    { to: '/ai-travel-assistant', icon: <FaRobot />, label: 'AI', color: 'from-violet-500 to-purple-600', badge: true },
  ];

  // Quick actions for profile dropdown - Updated with My Groups and Join Requests
  const quickActions = [
    { to: '/profile', icon: <FaUserCircle />, label: 'My Profile' },
    { to: '/my-groups', icon: <FaUsers />, label: 'My Groups' },
    { to: '/group-requests', icon: <FaUserPlus />, label: 'Join Requests' },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200/70 shadow-lg'
          : 'bg-white/90 backdrop-blur-lg border-b border-white/30'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* LEFT – LOGO with improved styling */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <img
                src={logo}
                alt="Travel Friend"
                className="w-12 h-12 lg:w-14 lg:h-14 object-contain rounded-xl shadow-md group-hover:shadow-xl transition-all duration-300 group-hover:scale-105"
              />
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-xl blur-md opacity-0 group-hover:opacity-30 transition-opacity" />
            </div>
            <div className="hidden sm:block">
              <div className="text-2xl font-bold">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Travel-Friend
                </span>
              </div>
              <div className="text-xs text-gray-500 font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                Connect • Explore • Share
              </div>
            </div>
          </Link>

          {/* RIGHT – ACTIONS */}
          <div className="flex items-center gap-2 lg:gap-3">
            {isAuthenticated ? (
              <>
                {/* Dark Mode Toggle (optional) */}
                <button
                  onClick={toggleDarkMode}
                  className="hidden lg:flex p-2.5 rounded-xl text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                  title="Toggle theme"
                >
                  {isDarkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
                </button>

                {/* Messages - Desktop */}
                <Link 
                  to="/private-chat" 
                  className="relative p-2.5 rounded-xl text-gray-600 hover:text-purple-600 hover:bg-purple-50 transition-all group"
                  title="Messages"
                >
                  <FaCommentDots size={20} />
                  {unreadMessageCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse">
                      {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                    </span>
                  )}
                </Link>

                {/* Notifications */}
                <Link 
                  to="/notifications" 
                  className="relative p-2.5 rounded-xl text-gray-600 hover:text-amber-600 hover:bg-amber-50 transition-all group"
                  title="Notifications"
                >
                  <FaBell size={20} />
                  {unreadNotificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
                      {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                    </span>
                  )}
                </Link>

                {/* Profile Dropdown - Fixed with proper container */}
                <div className="relative profile-menu-container">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center gap-2 px-2 lg:px-3 py-2 rounded-2xl hover:bg-gray-50 transition-all group"
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 flex items-center justify-center overflow-hidden border-2 border-transparent group-hover:border-indigo-200 transition-all">
                        {user?.profileImage ? (
                          <img src={user.profileImage} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          <FaUserCircle className="text-indigo-600 text-2xl" />
                        )}
                      </div>
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                    </div>
                    <div className="hidden lg:block text-left">
                      <div className="text-sm font-semibold text-gray-800">{user?.name?.split(' ')[0]}</div>
                      <div className="text-xs text-gray-500">Ready to explore</div>
                    </div>
                    <FaChevronDown className={`hidden lg:block text-gray-400 text-xs transition-transform duration-300 ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Profile Dropdown Menu - Fixed z-index and positioning */}
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-fadeIn">
                      {/* User Info */}
                      <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
                        <p className="font-semibold text-gray-800">{user?.name}</p>
                        <p className="text-sm text-gray-600 truncate">{user?.email}</p>
                      </div>

                      {/* Quick Actions */}
                      <div className="p-2">
                        {quickActions.map((action, index) => (
                          <Link
                            key={index}
                            to={action.to}
                            className="flex items-center gap-3 px-3 py-2.5 text-gray-700 hover:bg-indigo-50 rounded-xl transition-all"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <span className="text-indigo-500">{action.icon}</span>
                            <span className="text-sm font-medium">{action.label}</span>
                          </Link>
                        ))}
                      </div>

                      {/* Logout Button */}
                      <div className="p-2 border-t">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all"
                        >
                          <FaSignOutAlt />
                          <span className="text-sm font-medium">Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile Menu Toggle */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2.5 rounded-xl text-gray-600 hover:bg-gray-100 transition-all"
                >
                  <div className="w-5 h-4 flex flex-col justify-between">
                    <span className={`w-full h-0.5 bg-gray-600 transform transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
                    <span className={`w-full h-0.5 bg-gray-600 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
                    <span className={`w-full h-0.5 bg-gray-600 transform transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
                  </div>
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link 
                  to="/login" 
                  className="px-5 py-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-all font-medium"
                >
                  Sign In
                </Link>
                <Link 
                  to="/register" 
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium shadow-md hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* DESKTOP NAVIGATION - Centered */}
        {isAuthenticated && (
          <nav className="hidden lg:flex items-center justify-center gap-1 mt-2 pb-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`relative px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all group ${
                    isActive
                      ? `bg-gradient-to-r ${item.color} text-white shadow-md`
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className={`text-lg ${isActive ? 'text-white' : `group-hover:text-${item.color.split('-')[1]}-500`}`}>
                    {item.icon}
                  </span>
                  <span className="text-sm font-medium">{item.label}</span>
                  
                  {/* Badge for AI Assistant - Only show when not active */}
                  {item.badge && !isActive && (
                    <span className="absolute -top-1 -right-1 text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white px-1.5 py-0.5 rounded-full shadow-lg">
                      NEW
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        )}

        {/* MOBILE MENU */}
        {isAuthenticated && isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 mt-2 pt-2 pb-4 animate-slideDown">
            {/* Mobile Navigation */}
            <nav className="grid grid-cols-3 gap-2 px-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex flex-col items-center p-3 rounded-xl transition-all relative ${
                      isActive
                        ? `bg-gradient-to-r ${item.color} text-white`
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="text-xl mb-1">{item.icon}</span>
                    <span className="text-xs font-medium text-center">{item.label}</span>
                    {item.badge && !isActive && (
                      <span className="absolute -top-1 -right-1 text-[10px] bg-gradient-to-r from-purple-500 to-pink-500 text-white px-1.5 py-0.5 rounded-full">
                        NEW
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Mobile Profile Actions */}
            <div className="mt-4 pt-2 border-t border-gray-100 px-2">
              <div className="text-xs font-medium text-gray-500 mb-2 px-2">QUICK ACTIONS</div>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action, index) => (
                  <Link
                    key={index}
                    to={action.to}
                    className="flex items-center gap-2 px-3 py-2.5 text-gray-700 hover:bg-indigo-50 rounded-xl transition-all"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="text-indigo-500">{action.icon}</span>
                    <span className="text-xs font-medium">{action.label}</span>
                  </Link>
                ))}
              </div>
              
              {/* Mobile Logout */}
              <button
                onClick={handleLogout}
                className="w-full mt-2 flex items-center gap-2 px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all"
              >
                <FaSignOutAlt />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
      `}</style>
    </header>
  );
};

export default Header;